import React, { useCallback, useEffect, useMemo, useState } from "react";
import config from "../config.js";
import { detailToString } from '../utils/apiError';
import { emailSenderHeaders } from '../utils/emailSender';

/**
 * SERVICE RFQ — ask outside vendors what they charge, per piece, to run a process on parts
 * and/or ASSEMBLIES, with a vendor-facing document.
 *
 * RFQ used to be purchase-order-only: it could ask what a supplier charges for MATERIAL, and
 * nothing else. The first service RFQ (2026-08-25) could ask about PARTS, by email text alone.
 * This version (2026-09-03, Mike's decisions):
 *   - a line is a part OR an assembly — the coater prices the welded assembly (WLD304072),
 *     not the plate it is built on, and an assembly quote lands per ASSEMBLY;
 *   - any ACTIVE process can be asked about; the arming switch gates only the award
 *     write-through, so an un-armed process's price stays in the book and goes no further;
 *   - every send carries the RFQ DOCUMENT (GET /service_rfqs/{id}/pdf), addressed per vendor,
 *     with the reply address fixed to the shared inbox; drawings on file are attached ONLY when
 *     ticked here — customer prints can be confidential, so nothing is attached by default;
 *   - every send is recorded (GET /service_rfqs/{id}/emails) and the office can export the
 *     unaddressed document for printing or forwarding.
 *
 * Issuing emails every vendor selected in the modal; one vendor failing — no address, an SES
 * rejection — never stops the others, and sending again RE-SENDS, which is how the office
 * chases a coater who has gone quiet.
 */
const STATUS_STYLE = {
  Draft: "secondary", Sent: "info", Quoting: "warning",
  Awarded: "success", Cancelled: "dark",
};

// REQUIREMENTS seed per process. The document prints this verbatim under its own heading —
// it is where the process-specific words live, so the document itself can stay generic.
export const REQUIREMENT_TEMPLATES = {
  Coating:
    "Parts arrive laser-cut, formed and welded as applicable, free of oil beyond normal mill and forming residue.\n" +
    "Color: see the COLOR line on the document. Quote your standard system for the substrate listed on each item and note any premium for a custom match.\n" +
    "Pretreatment: your standard for the substrate listed on each item.\n" +
    "Masking: none required unless noted on the item.",
  Blast:
    "Blast to a uniform near-white finish unless noted on the item. Protect threads and machined faces called out on the item.",
  default:
    "Quote per piece as listed (per assembly for a welded assembly). Note any tooling, setup or minimum-lot charges separately.",
};

export const requirementsFor = (processName) =>
  REQUIREMENT_TEMPLATES[processName] || REQUIREMENT_TEMPLATES.default;

// A line's stable key across the UI, and its price-book scope on the wire.
export const lineKey = (l) => (l.assembly_id ? `a:${l.assembly_id}` : `p:${l.part_id}`);
export const lineScope = (l) => (l.assembly_id ? { assembly_id: l.assembly_id } : { part_id: l.part_id });

const fmtNum = (v, nd = 1) => (v === null || v === undefined || v === "" ? "—" : Number(v).toFixed(nd));
const envelope = (l) => {
  if (!l.size_length || !l.size_width) return "—";
  return `${Number(l.size_length).toFixed(1)} x ${Number(l.size_width).toFixed(1)}${l.thickness_in ? ` x ${Number(l.thickness_in).toFixed(3)}` : ""} in`;
};

// Vendors whose service_types mention the process (or the process's family word) first —
// a coater list is what the office expects when the process is Coating.
const vendorMatchesProcess = (v, processName) => {
  const st = String(v.service_types || "").toLowerCase();
  const p = String(processName || "").toLowerCase();
  if (!st || !p) return false;
  if (st.includes(p)) return true;
  if (p === "coating" && (st.includes("coat") || st.includes("powder") || st.includes("paint"))) return true;
  return false;
};

export default function ServiceRfqPage() {
  const API_BASE = config.API_BASE_URL;

  const [list, setList] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [rfq, setRfq] = useState(null);
  const [emails, setEmails] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  // create form
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newProcessId, setNewProcessId] = useState("");
  const [newDueBack, setNewDueBack] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [notesSeed, setNotesSeed] = useState("");
  const [newVendorIds, setNewVendorIds] = useState([]);
  const [partQuery, setPartQuery] = useState("");
  const [partHits, setPartHits] = useState([]);
  // Customer scope for the pickers (Mike, 2026-09-03): an RFQ is built for ONE customer's job,
  // and the office should not wade through everyone's parts and assemblies.
  const [customers, setCustomers] = useState([]);
  const [newCustomerId, setNewCustomerId] = useState("");
  const [customerParts, setCustomerParts] = useState([]);
  const [customerPartsLoading, setCustomerPartsLoading] = useState(false);
  const [newPartPick, setNewPartPick] = useState("");
  const [newAsmPick, setNewAsmPick] = useState("");
  const [newLines, setNewLines] = useState([]);

  // header edit
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editDue, setEditDue] = useState("");

  // email modal
  const [showEmail, setShowEmail] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailVendorIds, setEmailVendorIds] = useState([]);
  const [emailAttach, setEmailAttach] = useState([]);
  const [emailResult, setEmailResult] = useState(null);

  // quote entry
  const [quoteVendor, setQuoteVendor] = useState("");
  const [quoteRows, setQuoteRows] = useState({});

  const loadList = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/service_rfqs`);
      setList(r.ok ? await r.json() : []);
    } catch (e) { setError(e.message); }
  }, [API_BASE]);

  const loadRefs = useCallback(async () => {
    try {
      const [v, p, a, c] = await Promise.all([
        fetch(`${API_BASE}/vendors`),
        fetch(`${API_BASE}/processes`),
        fetch(`${API_BASE}/assemblies`),
        fetch(`${API_BASE}/customers`),
      ]);
      const allV = v.ok ? await v.json() : [];
      setVendors(allV.filter((x) => x.is_service));
      const allP = p.ok ? await p.json() : [];
      // Any ACTIVE process may be asked about (Mike, 2026-09-03). Shipping is a station, not
      // work anyone quotes, so it stays out of the picker.
      setProcesses(allP.filter((x) => x.active !== false && x.name !== "Shipping"));
      const allA = a.ok ? await a.json() : [];
      setAssemblies(Array.isArray(allA) ? allA : []);
      const allC = c.ok ? await c.json() : [];
      setCustomers((Array.isArray(allC) ? allC : [])
        .slice().sort((x, y) => String(x.name || "").localeCompare(String(y.name || ""))));
    } catch (e) { /* references are best-effort */ }
  }, [API_BASE]);

  const loadOne = useCallback(async (id) => {
    if (!id) { setRfq(null); setEmails([]); return; }
    try {
      const [r, e] = await Promise.all([
        fetch(`${API_BASE}/service_rfqs/${id}`),
        fetch(`${API_BASE}/service_rfqs/${id}/emails`),
      ]);
      setRfq(r.ok ? await r.json() : null);
      setEmails(e.ok ? await e.json() : []);
    } catch (e) { setError(e.message); }
  }, [API_BASE]);

  useEffect(() => { loadList(); loadRefs(); }, [loadList, loadRefs]);
  useEffect(() => { loadOne(openId); setEditing(false); setQuoteRows({}); }, [openId, loadOne]);

  const post = async (path, body, method = "POST", extraHeaders = {}) => {
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API_BASE}${path}`, {
        method, headers: { "Content-Type": "application/json", ...extraHeaders },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { setError(detailToString(data.detail) || `Failed (${r.status}).`); return null; }
      return data;
    } catch (e) { setError(e.message); return null; }
    finally { setBusy(false); }
  };

  // The API key rides on fetch (config.js wraps it), not on a bare window.open, so a PDF is
  // fetched as a blob first. Same pattern as the PO and OPO screens.
  const openPdf = async (url, filename) => {
    setError("");
    try {
      const r = await fetch(url);
      if (!r.ok) { setError(`Could not build the PDF (${r.status}).`); return; }
      const blob = await r.blob();
      const u = window.URL.createObjectURL(blob);
      if (filename) {
        const a = document.createElement("a");
        a.href = u; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
      } else {
        window.open(u, "_blank");
      }
    } catch (e) { setError(e.message); }
  };

  const newProcessName = useMemo(
    () => (processes.find((p) => String(p.process_id) === String(newProcessId)) || {}).name || "",
    [processes, newProcessId]);

  const pickProcess = (pid) => {
    setNewProcessId(pid);
    const name = (processes.find((p) => String(p.process_id) === String(pid)) || {}).name || "";
    const seed = requirementsFor(name);
    // Seed the requirements from the process template — but never over the operator's own
    // words: only when the box is empty or still holds the previous template.
    if (!newNotes.trim() || newNotes === notesSeed) { setNewNotes(seed); }
    setNotesSeed(seed);
  };

  const searchParts = async (q) => {
    setPartQuery(q);
    if (q.trim().length < 2) { setPartHits([]); return; }
    try {
      // The parameter is `query`, not `q` — GET /parts/search declares search_parts(query: str).
      const scope = newCustomerId ? `&customer_id=${encodeURIComponent(newCustomerId)}` : "";
      const r = await fetch(`${API_BASE}/parts/search?query=${encodeURIComponent(q)}${scope}`);
      setPartHits(r.ok ? (await r.json()).slice(0, 8) : []);
    } catch (e) { setPartHits([]); }
  };

  // Assemblies as a DROPDOWN (Mike, 2026-09-03), scoped to the chosen customer; inactive never offered.
  const asmOptions = useMemo(() => assemblies
    .filter((a) => a.active_status !== false)
    .filter((a) => !newCustomerId || String(a.customer_id) === String(newCustomerId))
    .slice().sort((a, b) => String(a.assembly_name || "").localeCompare(String(b.assembly_name || ""))),
  [assemblies, newCustomerId]);

  const pickCustomer = async (cid) => {
    setNewCustomerId(cid);
    setPartHits([]); setPartQuery(""); setNewPartPick(""); setNewAsmPick("");
    setCustomerParts([]);
    if (!cid) return;
    setCustomerPartsLoading(true);
    try {
      // An empty query with a customer lists that customer's parts — what fills the dropdown.
      const r = await fetch(`${API_BASE}/parts/search?query=&customer_id=${encodeURIComponent(cid)}&limit=200`);
      const rows = r.ok ? await r.json() : [];
      setCustomerParts(Array.isArray(rows) ? rows : []);
    } catch (e) { setCustomerParts([]); }
    finally { setCustomerPartsLoading(false); }
  };

  const addLine = (line) => {
    setNewLines((prev) => prev.some((l) => lineKey(l) === lineKey(line)) ? prev : [...prev, line]);
  };

  const createRfq = async () => {
    if (!newProcessId || newLines.length === 0) {
      setError("Pick a process and at least one part or assembly."); return;
    }
    const data = await post("/service_rfqs", {
      process_id: Number(newProcessId), title: newTitle || null,
      notes: newNotes || null, due_back: newDueBack || null,
      vendor_ids: newVendorIds.map(Number),
      lines: newLines.map((l) => ({ ...lineScope(l), quantity: Number(l.quantity) || 0, notes: l.notes || null })),
    });
    if (data) {
      setShowNew(false); setNewTitle(""); setNewLines([]); setNewVendorIds([]);
      setNewNotes(""); setNotesSeed(""); setNewDueBack(""); setNewProcessId("");
      setNewCustomerId(""); setCustomerParts([]); setNewPartPick(""); setNewAsmPick("");
      await loadList(); setOpenId(data.rfq_id);
    }
  };

  const saveHeader = async () => {
    const data = await post(`/service_rfqs/${rfq.rfq_id}`,
      { title: editTitle, notes: editNotes, due_back: editDue || "" }, "PATCH");
    if (data) { setRfq(data); setEditing(false); await loadList(); }
  };

  const openEmailModal = () => {
    setEmailSubject(`${rfq.rfq_number} — Request for Quote — ${rfq.process_name} — KanCraft MFG`);
    setEmailBody(
      `Hello,\n\nPlease find attached ${rfq.rfq_number}, our request for a per-piece price for ` +
      `${String(rfq.process_name || "").toLowerCase()} on the items listed in the document` +
      `${rfq.due_back ? `. Please reply by ${rfq.due_back}` : ""}.\n\n` +
      `Please quote a price per piece for each line (per assembly for a welded assembly), your normal ` +
      `turnaround in business days, and any setup, minimum-lot or handling charges.\n\n` +
      `Reply to this email or to ${rfq.reply_email || "info@kancraftmfg.com"}, referencing ${rfq.rfq_number}.\n\n` +
      `Thank you,\nKanCraft MFG`);
    setEmailVendorIds(rfq.vendors.filter((v) => v.vendor_email).map((v) => v.vendor_id));
    setEmailAttach([]);
    setEmailResult(null);
    setShowEmail(true);
  };

  const sendEmail = async () => {
    if (emailVendorIds.length === 0) { setError("Pick at least one vendor to send to."); return; }
    const res = await post(`/service_rfqs/${rfq.rfq_id}/issue`, {
      subject: emailSubject, body_text: emailBody,
      vendor_ids: emailVendorIds, attachments: emailAttach,
    }, "POST", emailSenderHeaders());
    if (res) {
      setEmailResult(res);
      // Say who actually got it. "Sent" with a silent failure is how the office ends up
      // waiting on a coater who was never asked.
      if (res.failed_count) setError(`${res.message} — see the red addresses below.`);
      else setNotice(res.message);
      await loadOne(rfq.rfq_id); await loadList();
    }
  };

  const docsOnRfq = useMemo(() => {
    if (!rfq) return [];
    const out = [];
    rfq.lines.forEach((l) => (l.documents || []).forEach((d) => out.push({ ...d, item: l.item_code, kind: l.item_kind })));
    return out;
  }, [rfq]);

  const totalsByVendor = () => {
    if (!rfq) return [];
    const qty = {}; rfq.lines.forEach((l) => { qty[lineKey(l)] = l.quantity; });
    const t = {};
    rfq.quotes.forEach((q) => {
      t[q.vendor_id] = t[q.vendor_id] || { name: q.vendor_name, total: 0, items: 0 };
      t[q.vendor_id].total += q.price_per_piece * (qty[lineKey(q)] || 0);
      t[q.vendor_id].items += 1;
    });
    return Object.entries(t).map(([id, v]) => ({ vendor_id: Number(id), ...v }))
      .sort((a, b) => a.total - b.total);
  };

  const isOpen = rfq && rfq.status !== "Awarded" && rfq.status !== "Cancelled";
  const sortedVendors = (processName) => [...vendors].sort((a, b) =>
    (vendorMatchesProcess(b, processName) ? 1 : 0) - (vendorMatchesProcess(a, processName) ? 1 : 0)
    || String(a.name).localeCompare(String(b.name)));

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Service RFQs</h4>
        <button className="btn btn-primary btn-sm" onClick={() => setShowNew((v) => !v)}>
          {showNew ? "Cancel" : "New RFQ"}
        </button>
      </div>
      {error && <div className="alert alert-danger py-2">{error}</div>}
      {notice && !error && <div className="alert alert-success py-2">{notice}</div>}

      {showNew && (
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 mb-2">
              <div className="col-md-4">
                <label className="form-label small fw-bold">Title (printed as "For" on the document)</label>
                <input className="form-control form-control-sm" value={newTitle}
                       onChange={(e) => setNewTitle(e.target.value)}
                       placeholder="e.g. WLD304072 — powder coat, customer 38" />
              </div>
              <div className="col-md-2">
                <label className="form-label small fw-bold">Process</label>
                <select className="form-select form-select-sm" value={newProcessId}
                        onChange={(e) => pickProcess(e.target.value)} aria-label="Process">
                  <option value="">— pick —</option>
                  {processes.map((p) => (
                    <option key={p.process_id} value={p.process_id}>
                      {p.name}{p.is_outsourceable ? "" : " (in-house — price only)"}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label small fw-bold">Quote needed by</label>
                <input type="date" className="form-control form-control-sm" value={newDueBack}
                       onChange={(e) => setNewDueBack(e.target.value)} aria-label="Quote needed by" />
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-bold">Vendors to ask</label>
                <div className="d-flex flex-wrap gap-2">
                  {sortedVendors(newProcessName).map((v) => (
                    <div className="form-check" key={v.vendor_id}>
                      <input className="form-check-input" type="checkbox" id={`nv-${v.vendor_id}`}
                             checked={newVendorIds.includes(String(v.vendor_id))}
                             onChange={(e) => setNewVendorIds((prev) =>
                               e.target.checked ? [...prev, String(v.vendor_id)]
                                                : prev.filter((x) => x !== String(v.vendor_id)))} />
                      <label className="form-check-label small" htmlFor={`nv-${v.vendor_id}`}>
                        {v.name}
                        {vendorMatchesProcess(v, newProcessName) && <span className="badge bg-light text-dark ms-1">{v.service_types}</span>}
                        {!v.email && <span className="text-danger" title="No email on file"> ⚠ no email</span>}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="row g-2 mb-2">
              <div className="col-md-12">
                <label className="form-label small fw-bold">Requirements (printed verbatim on the document)</label>
                <textarea className="form-control form-control-sm" rows="4" value={newNotes}
                          onChange={(e) => setNewNotes(e.target.value)} aria-label="Requirements" />
              </div>
            </div>
            <div className="row g-2">
              <div className="col-md-3">
                <label className="form-label small fw-bold">Customer</label>
                <select className="form-select form-select-sm" value={newCustomerId} aria-label="Customer"
                        onChange={(e) => pickCustomer(e.target.value)}>
                  <option value="">— all customers —</option>
                  {customers.map((c) => (
                    <option key={c.customer_id} value={c.customer_id}>{c.name}</option>
                  ))}
                </select>
                <div className="form-text">Scopes the part and assembly pickers.</div>
              </div>
              <div className="col-md-5">
                <label className="form-label small fw-bold">Add a part</label>
                <select className="form-select form-select-sm mb-1" value={newPartPick} aria-label="Add a part"
                        disabled={!newCustomerId || customerPartsLoading}
                        onChange={(e) => {
                          const h = customerParts.find((x) => String(x.part_id) === e.target.value);
                          if (h) addLine({ kind: "part", part_id: h.part_id, code: h.product_id, description: h.description, quantity: 0, notes: "" });
                          setNewPartPick("");
                        }}>
                  <option value="">
                    {!newCustomerId ? "— pick a customer to list their parts —"
                      : customerPartsLoading ? "— loading parts… —"
                      : customerParts.length ? `— pick from ${customerParts.length} part${customerParts.length === 1 ? "" : "s"} —`
                      : "— no active parts for this customer —"}
                  </option>
                  {customerParts.map((h) => (
                    <option key={h.part_id} value={h.part_id}>{h.product_id} — {h.description}</option>
                  ))}
                </select>
                <input className="form-control form-control-sm" value={partQuery}
                       onChange={(e) => searchParts(e.target.value)} aria-label="Search parts"
                       placeholder={newCustomerId ? "…or search this customer's parts" : "…or search all parts by product id"} />
                {partHits.length > 0 && (
                  <div className="list-group mt-1">
                    {partHits.map((h) => (
                      <button type="button" key={h.part_id}
                              className="list-group-item list-group-item-action py-1 small"
                              onClick={() => {
                                addLine({ kind: "part", part_id: h.part_id, code: h.product_id, description: h.description, quantity: 0, notes: "" });
                                setPartQuery(""); setPartHits([]);
                              }}>
                        {h.product_id} — {h.description}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-bold">Add an assembly</label>
                <select className="form-select form-select-sm" value={newAsmPick} aria-label="Add an assembly"
                        onChange={(e) => {
                          const a = asmOptions.find((x) => String(x.assembly_id) === e.target.value);
                          if (a) addLine({ kind: "assembly", assembly_id: a.assembly_id, code: a.assembly_name,
                                           description: `on ${(a.primary_part || {}).product_id || "primary part"}`, quantity: 0, notes: "" });
                          setNewAsmPick("");
                        }}>
                  <option value="">
                    {asmOptions.length ? `— pick from ${asmOptions.length} assembl${asmOptions.length === 1 ? "y" : "ies"} —`
                      : "— no active assemblies for this customer —"}
                  </option>
                  {asmOptions.map((a) => (
                    <option key={a.assembly_id} value={a.assembly_id}>
                      {a.assembly_name} — on {(a.primary_part || {}).product_id || "?"}
                    </option>
                  ))}
                </select>
                <div className="form-text">Welded assemblies are quoted per assembly.</div>
              </div>
            </div>
            <div className="row g-2 mt-1">
              <div className="col-md-12">
                <label className="form-label small fw-bold">On this RFQ</label>
                {newLines.length === 0 && <div className="text-muted small">No items yet.</div>}
                {newLines.map((l, i) => (
                  <div className="d-flex align-items-center gap-2 mb-1" key={lineKey(l)}>
                    <span className="small flex-grow-1">
                      {l.kind === "assembly" && <span className="badge bg-dark me-1">assembly</span>}
                      {l.code} <span className="text-muted">{l.description}</span>
                    </span>
                    <input type="number" min="0" className="form-control form-control-sm"
                           style={{ width: "7rem" }} value={l.quantity} aria-label={`Quantity for ${l.code}`}
                           placeholder={l.kind === "assembly" ? "sets" : "pieces"}
                           onChange={(e) => setNewLines((prev) =>
                             prev.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))} />
                    <input className="form-control form-control-sm" style={{ width: "14rem" }} value={l.notes}
                           placeholder="line note (racking, masking…)"
                           onChange={(e) => setNewLines((prev) =>
                             prev.map((x, j) => j === i ? { ...x, notes: e.target.value } : x))} />
                    <button className="btn btn-sm btn-outline-danger"
                            onClick={() => setNewLines((prev) => prev.filter((_, j) => j !== i))}>×</button>
                  </div>
                ))}
              </div>
            </div>
            <button className="btn btn-success btn-sm mt-2" disabled={busy} onClick={createRfq}>
              Open RFQ
            </button>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-md-4">
          <table className="table table-sm table-hover">
            <thead><tr><th>RFQ</th><th>Process</th><th>Status</th><th>Items</th><th>Sent</th></tr></thead>
            <tbody>
              {list.length === 0 && (
                <tr><td colSpan="5" className="text-muted small">No RFQs yet.</td></tr>
              )}
              {list.map((r) => (
                <tr key={r.rfq_id} onClick={() => setOpenId(r.rfq_id)} style={{ cursor: "pointer" }}
                    className={openId === r.rfq_id ? "table-active" : ""}>
                  <td className="small">{r.rfq_number}<div className="text-muted" style={{ fontSize: "0.7rem" }}>{r.title}</div></td>
                  <td className="small">{r.process_name}</td>
                  <td><span className={`badge bg-${STATUS_STYLE[r.status] || "secondary"}`}>{r.status}</span></td>
                  <td className="small">{r.line_count}{r.assembly_count ? <span className="text-muted"> ({r.assembly_count} asm)</span> : null}</td>
                  <td className="small">{r.last_sent_at ? new Date(r.last_sent_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="col-md-8">
          {!rfq && <div className="text-muted small">Pick an RFQ to see its items, vendors and quotes.</div>}
          {rfq && (
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-start">
                <div>
                  <strong>{rfq.rfq_number}</strong>{" "}
                  <span className={`badge bg-${STATUS_STYLE[rfq.status] || "secondary"}`}>{rfq.status}</span>
                  <span className="badge bg-light text-dark ms-2">{rfq.process_name}</span>
                  {!rfq.process_outsourceable && (
                    <span className="badge bg-warning text-dark ms-1"
                          title="This process is not armed as outsourceable. Prices are recorded in the book on award but not applied to the item.">
                      price only
                    </span>
                  )}
                  <div className="small text-muted">{rfq.title}</div>
                  <div className="small text-muted">
                    {rfq.due_back ? `quote needed by ${rfq.due_back}` : "no reply-by date"}
                    {rfq.created_by ? ` · ${rfq.created_by}` : ""}
                  </div>
                </div>
                <div className="d-flex gap-2 flex-wrap justify-content-end">
                  <button className="btn btn-sm btn-outline-secondary" disabled={busy}
                          onClick={() => openPdf(`${API_BASE}/service_rfqs/${rfq.rfq_id}/pdf`)}>
                    Preview PDF
                  </button>
                  <button className="btn btn-sm btn-outline-secondary" disabled={busy}
                          title="Unaddressed copy for printing or forwarding"
                          onClick={() => openPdf(`${API_BASE}/service_rfqs/${rfq.rfq_id}/pdf`, `${rfq.rfq_number}.pdf`)}>
                    Export PDF
                  </button>
                  {isOpen && (
                    <button className="btn btn-sm btn-primary" disabled={busy} onClick={openEmailModal}>
                      {rfq.vendors.some((v) => v.sent_at) ? "Email vendors again…" : "Email vendors…"}
                    </button>
                  )}
                  {isOpen && !editing && (
                    <button className="btn btn-sm btn-outline-secondary" disabled={busy}
                            onClick={() => { setEditTitle(rfq.title || ""); setEditNotes(rfq.notes || ""); setEditDue(rfq.due_back || ""); setEditing(true); }}>
                      Edit
                    </button>
                  )}
                  {isOpen && (
                    <button className="btn btn-sm btn-outline-danger" disabled={busy}
                            onClick={async () => {
                              if (!window.confirm(`Cancel ${rfq.rfq_number}? Prices already recorded stay in the book.`)) return;
                              const res = await post(`/service_rfqs/${rfq.rfq_id}/cancel`);
                              if (res) { await loadOne(rfq.rfq_id); await loadList(); }
                            }}>
                      Cancel RFQ
                    </button>
                  )}
                </div>
              </div>
              <div className="card-body">
                {editing && (
                  <div className="border rounded p-2 mb-3">
                    <div className="row g-2">
                      <div className="col-md-8">
                        <label className="form-label small fw-bold">Title</label>
                        <input className="form-control form-control-sm" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-bold">Quote needed by</label>
                        <input type="date" className="form-control form-control-sm" value={editDue} onChange={(e) => setEditDue(e.target.value)} />
                      </div>
                      <div className="col-md-12">
                        <label className="form-label small fw-bold">Requirements (printed verbatim on the document)</label>
                        <textarea className="form-control form-control-sm" rows="4" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
                      </div>
                    </div>
                    <div className="mt-2 d-flex gap-2">
                      <button className="btn btn-sm btn-success" disabled={busy} onClick={saveHeader}>Save</button>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditing(false)}>Cancel</button>
                    </div>
                  </div>
                )}

                <div className="fw-bold small mb-1">Items</div>
                <table className="table table-sm mb-3">
                  <thead>
                    <tr><th>Item</th><th className="text-end">Qty</th><th>Envelope L x W x T</th><th>Material</th>
                        <th className="text-end">Lb ea</th><th>Color</th><th>Drawings</th></tr>
                  </thead>
                  <tbody>
                    {rfq.lines.map((l) => (
                      <tr key={l.rfq_line_id}>
                        <td className="small">
                          {l.item_kind === "assembly" && <span className="badge bg-dark me-1">assembly</span>}
                          <strong>{l.item_code}</strong>{l.revision ? ` Rev ${l.revision}` : ""}
                          <div className="text-muted" style={{ fontSize: "0.72rem" }}>{l.description}</div>
                          {l.notes && <div className="text-muted fst-italic" style={{ fontSize: "0.72rem" }}>{l.notes}</div>}
                        </td>
                        <td className="small text-end">{l.quantity}{l.item_kind === "assembly" ? " sets" : ""}</td>
                        <td className="small">{envelope(l)}{l.envelope_source && <span className="text-muted" title={l.envelope_source}> *</span>}</td>
                        <td className="small">{l.material || "—"}</td>
                        <td className="small text-end">{fmtNum(l.weight_lbs, 2)}</td>
                        <td className="small">
                          {l.color_code ? (
                            <span>
                              {l.color_hex && <span style={{ display: "inline-block", width: 10, height: 10, background: l.color_hex, border: "1px solid #333", marginRight: 4 }} />}
                              {l.color_code}
                            </span>
                          ) : <span className="text-muted">—</span>}
                        </td>
                        <td className="small">{(l.documents || []).length ? `${l.documents.length} on file` : <span className="text-muted">none</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="mb-3">
                  <div className="fw-bold small mb-1">Asked</div>
                  {rfq.vendors.length === 0 && <div className="text-muted small">No vendors on this RFQ yet — Edit to add some.</div>}
                  {rfq.vendors.map((v) => (
                    <div key={v.rfq_vendor_id} className="small">
                      {v.vendor_name}
                      {v.vendor_email
                        ? <span className="ms-2 text-muted">{v.vendor_email}</span>
                        : <span className="text-danger ms-2">no email on file — cannot be asked</span>}
                      {v.send_error
                        ? <span className="text-danger ms-2" title={v.send_error}>⚠ not delivered</span>
                        : v.sent_at && <span className="badge bg-secondary ms-2">sent {new Date(v.sent_at).toLocaleDateString()}</span>}
                      {v.responded_at && <span className="badge bg-success ms-2">responded</span>}
                      <button className="btn btn-link btn-sm py-0" onClick={() => openPdf(`${API_BASE}/service_rfqs/${rfq.rfq_id}/pdf?vendor_id=${v.vendor_id}`)}>
                        PDF
                      </button>
                    </div>
                  ))}
                </div>

                {totalsByVendor().length > 0 && (
                  <div className="mb-3">
                    <div className="fw-bold small mb-1">Quoted totals (across the quantities asked)</div>
                    <table className="table table-sm mb-0">
                      <tbody>
                        {totalsByVendor().map((t, i) => (
                          <tr key={t.vendor_id}>
                            <td className="small">{i === 0 && <span className="badge bg-success me-1">lowest</span>}{t.name}</td>
                            <td className="small text-end">${t.total.toFixed(2)}</td>
                            <td className="small text-muted">{t.items} of {rfq.lines.length} items</td>
                            <td className="text-end">
                              {isOpen && (
                                <button className="btn btn-sm btn-success" disabled={busy}
                                        onClick={async () => {
                                          const res = await post(`/service_rfqs/${rfq.rfq_id}/award`, { vendor_id: t.vendor_id });
                                          if (res) { setNotice(res.message); await loadOne(rfq.rfq_id); await loadList(); }
                                        }}>Award</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {rfq.status !== "Cancelled" && (
                  <>
                    <div className="fw-bold small mb-1">Record a quote</div>
                    <div className="d-flex gap-2 align-items-center mb-2">
                      <select className="form-select form-select-sm" style={{ width: "16rem" }} aria-label="Which vendor answered"
                              value={quoteVendor} onChange={(e) => setQuoteVendor(e.target.value)}>
                        <option value="">— which vendor answered —</option>
                        {rfq.vendors.map((v) => (
                          <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>
                        ))}
                      </select>
                      <button className="btn btn-sm btn-primary" disabled={busy || !quoteVendor}
                              onClick={async () => {
                                const lines = rfq.lines
                                  .filter((l) => quoteRows[lineKey(l)] !== undefined && quoteRows[lineKey(l)] !== "")
                                  .map((l) => ({ ...lineScope(l), price_per_piece: Number(quoteRows[lineKey(l)]) }));
                                if (lines.length === 0) { setError("Enter at least one price."); return; }
                                const res = await post(`/service_rfqs/${rfq.rfq_id}/quotes`,
                                                       { vendor_id: Number(quoteVendor), lines });
                                if (res) { setQuoteRows({}); await loadOne(rfq.rfq_id); await loadList(); }
                              }}>Save quote</button>
                    </div>
                    <table className="table table-sm">
                      <thead>
                        <tr><th>Item</th><th className="text-end">Qty asked</th>
                            <th style={{ width: "9rem" }}>$ per piece / assembly</th><th>Prices on file</th></tr>
                      </thead>
                      <tbody>
                        {rfq.lines.map((l) => (
                          <tr key={l.rfq_line_id}>
                            <td className="small">
                              {l.item_kind === "assembly" && <span className="badge bg-dark me-1">assembly</span>}{l.item_code}
                            </td>
                            <td className="small text-end">{l.quantity}</td>
                            <td>
                              <input type="number" step="0.0001" min="0" className="form-control form-control-sm"
                                     aria-label={`Price for ${l.item_code}`}
                                     value={quoteRows[lineKey(l)] ?? ""}
                                     onChange={(e) => setQuoteRows((p) => ({ ...p, [lineKey(l)]: e.target.value }))} />
                            </td>
                            <td className="small">
                              {rfq.quotes.filter((q) => lineKey(q) === lineKey(l)).map((q) => (
                                <span key={q.vendor_id} className="me-2">
                                  {q.is_preferred && <span className="badge bg-success me-1">✓</span>}
                                  {q.vendor_name}: ${q.price_per_piece.toFixed(4)}
                                  {q.source === "awarded" && <span className="badge bg-success ms-1">awarded</span>}
                                </span>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                <div className="fw-bold small mb-1 mt-2">Email history</div>
                {emails.length === 0 && <div className="text-muted small">Nothing sent yet.</div>}
                {emails.length > 0 && (
                  <table className="table table-sm mb-0">
                    <thead><tr><th>Sent</th><th>To</th><th>Subject</th><th>Attached</th><th>Status</th></tr></thead>
                    <tbody>
                      {emails.map((e) => (
                        <tr key={e.email_id}>
                          <td className="small">{e.sent_at ? new Date(e.sent_at).toLocaleString() : "—"}</td>
                          <td className="small">{e.vendor_name}<div className="text-muted" style={{ fontSize: "0.7rem" }}>{e.to_email}</div></td>
                          <td className="small">{e.subject}</td>
                          <td className="small">{e.attachments || "—"}</td>
                          <td className="small">
                            {e.status === "sent"
                              ? <span className="badge bg-success">sent</span>
                              : <span className="badge bg-danger" title={e.error || ""}>failed</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showEmail && rfq && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }} role="dialog">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Email {rfq.rfq_number} to vendors</h5>
                <button type="button" className="btn-close" onClick={() => setShowEmail(false)} aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <div className="mb-2">
                  <label className="form-label small fw-bold">Send to</label>
                  {rfq.vendors.map((v) => (
                    <div className="form-check" key={v.vendor_id}>
                      <input className="form-check-input" type="checkbox" id={`ev-${v.vendor_id}`}
                             disabled={!v.vendor_email}
                             checked={emailVendorIds.includes(v.vendor_id)}
                             onChange={(e) => setEmailVendorIds((prev) =>
                               e.target.checked ? [...prev, v.vendor_id] : prev.filter((x) => x !== v.vendor_id))} />
                      <label className="form-check-label small" htmlFor={`ev-${v.vendor_id}`}>
                        {v.vendor_name}{" "}
                        {v.vendor_email
                          ? <span className="text-muted">{v.vendor_email}</span>
                          : <span className="text-danger">no email on file</span>}
                        {v.sent_at && <span className="badge bg-secondary ms-2">sent before</span>}
                      </label>
                    </div>
                  ))}
                </div>
                <div className="mb-2">
                  <label className="form-label small fw-bold">Subject</label>
                  <input className="form-control form-control-sm" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} aria-label="Subject" />
                </div>
                <div className="mb-2">
                  <label className="form-label small fw-bold">Message</label>
                  <textarea className="form-control form-control-sm" rows="7" value={emailBody} onChange={(e) => setEmailBody(e.target.value)} aria-label="Message" />
                  <small className="text-muted">The RFQ document, addressed to each vendor, is attached automatically.</small>
                </div>
                <div className="mb-2">
                  <label className="form-label small fw-bold">Also attach drawings on file</label>
                  {docsOnRfq.length === 0 && <div className="text-muted small">No drawings on file for these items. The note says drawings are available on request.</div>}
                  {docsOnRfq.map((d) => (
                    <div className="form-check" key={d.token}>
                      <input className="form-check-input" type="checkbox" id={`att-${d.token}`}
                             checked={emailAttach.includes(d.token)}
                             onChange={(e) => setEmailAttach((prev) =>
                               e.target.checked ? [...prev, d.token] : prev.filter((x) => x !== d.token))} />
                      <label className="form-check-label small" htmlFor={`att-${d.token}`}>
                        {d.name} <span className="text-muted">— {d.item}</span>
                      </label>
                    </div>
                  ))}
                  {docsOnRfq.length > 0 && (
                    <div className="form-text">
                      Nothing is attached unless ticked: a customer's print can be confidential. Untick anything the vendor should not hold.
                    </div>
                  )}
                </div>
                {emailResult && (
                  <div className="border rounded p-2 small">
                    <div className="fw-bold">{emailResult.message}</div>
                    {emailResult.vendors.map((r) => (
                      <div key={r.vendor_id} className={r.delivered ? "" : "text-danger"}>
                        {r.delivered ? "✓" : "✗"} {r.vendor_name} {r.vendor_email ? `(${r.vendor_email})` : ""}
                        {r.send_error ? ` — ${r.send_error}` : (r.attachments && r.attachments.length ? ` — attached: ${r.attachments.join(", ")}` : "")}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEmail(false)} disabled={busy}>Close</button>
                <button type="button" className="btn btn-primary" onClick={sendEmail} disabled={busy || emailVendorIds.length === 0}>
                  {busy ? "Sending…" : `Send to ${emailVendorIds.length} vendor${emailVendorIds.length === 1 ? "" : "s"}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
